/**
 * ════════════════════════════════════════════════════════════════════════════
 * WHERE A STAMP MARK LANDS, AND HOW BIG IT IS (T-125)
 *
 * The one piece of arithmetic the placement editor and the stamping engine must agree on.
 * They used to compute it separately: the editor guessed an image's height as width × 0.3
 * and estimated text at half an em per character, while the engine used the image's real
 * proportions and the font's real metrics. A seal placed at 150 × 45 printed at 150 × 124,
 * grew upward into the date, and nobody could see it happening until the proof.
 *
 * Both sides now call these functions. What differs between them is only *where the inputs
 * come from* — the engine reads the embedded image and pdf-lib's font; the editor reads the
 * loaded <img> and the same pdf-lib font embedder in the browser — never the arithmetic.
 *
 * Everything is PDF points from the BOTTOM-LEFT of the page, like the saved slot itself.
 * Pure: no pdf-lib, no DOM, so it runs unchanged on both sides.
 * ════════════════════════════════════════════════════════════════════════════
 */
/**
 * The printed width of a seal or signature when a slot does not say.
 *
 * The engine used to fall back to the image's PIXEL width, read as points — an 808px
 * signature scan printed 808pt wide, wider than an A4 page. The editor had always drawn
 * 140, so that is the width both now agree on.
 */
export const DEFAULT_IMAGE_MARK_WIDTH = 140;
/**
 * Which standard font a slot's `fontFamily` prints in.
 *
 * Moved here verbatim from the engine's `pickFont()`, including its one quirk: the test for
 * Courier comes first, so "Courier-Bold" prints in regular Courier. Changing that would
 * change how every existing certificate number looks, which is a decision rather than a
 * refactor — so it is preserved and written down.
 */
export function markFontFor(family) {
    const f = family.toLowerCase();
    if (f.includes('courier') || f.includes('mono'))
        return 'Courier';
    if (f.includes('times') || f.includes('serif'))
        return 'Times-Roman';
    if (f.includes('bold'))
        return 'Helvetica-Bold';
    return 'Helvetica';
}
export function isImageMark(slotType) {
    return slotType === 'SEAL' || slotType === 'SIGNATURE';
}
/**
 * An image mark's printed box, from the image's real pixel size.
 *
 * Proportions are kept whenever only one side is set — a seal squashed to fit a box stops
 * looking like the chamber's seal, which is the one thing a person checking the
 * certificate examines. Both sides set is honoured as-is, for layouts saved that way.
 */
export function imageMarkBox(slot, image) {
    const ratio = image.width > 0 ? image.height / image.width : 1;
    let width;
    let height;
    if (slot.width && slot.height) {
        width = slot.width;
        height = slot.height;
    }
    else if (slot.height) {
        height = slot.height;
        width = height / ratio;
    }
    else {
        width = slot.width ?? DEFAULT_IMAGE_MARK_WIDTH;
        height = width * ratio;
    }
    return { x: slot.x, y: slot.y, width, height };
}
/**
 * A text mark's layout.
 *
 * Two ways to say where text goes, both kept:
 *
 *   NO WIDTH — the saved x is an anchor: the left edge, the centre, or the right edge,
 *   depending on alignment. This is how every layout before T-126 was saved, and it prints
 *   exactly as it always has.
 *
 *   A WIDTH — the saved x is the left of a box of that width, and alignment places the text
 *   inside it. Resizing a text mark on the canvas sets this. A centred date stays centred in
 *   its box when a longer month name widens it, which an anchor can also do — but a box is
 *   what the administrator can see and drag.
 *
 * Vertically the saved y is the baseline, so the box runs from the descender up.
 */
export function textMarkLayout(slot, measure) {
    const align = slot.align ?? 'LEFT';
    const boxWidth = slot.width ?? null;
    let drawX;
    let box;
    if (boxWidth) {
        drawX =
            align === 'CENTER'
                ? slot.x + (boxWidth - measure.width) / 2
                : align === 'RIGHT'
                    ? slot.x + boxWidth - measure.width
                    : slot.x;
        box = { x: slot.x, y: slot.y - measure.descent, width: boxWidth, height: measure.height };
    }
    else {
        drawX =
            align === 'CENTER' ? slot.x - measure.width / 2 : align === 'RIGHT' ? slot.x - measure.width : slot.x;
        box = { x: drawX, y: slot.y - measure.descent, width: measure.width, height: measure.height };
    }
    return { box, drawX, baseline: slot.y };
}
/**
 * Moves a box back inside the page, keeping its size.
 *
 * The engine does this to every image mark — silently losing the chamber's seal off an edge
 * is the worst available failure, since the PDF is valid and the certificate is simply
 * unsigned. The editor applies the same clamp when it draws, so a mark resized past the
 * edge is shown where it will actually print rather than where it was left.
 */
export function clampMarkBox(box, page) {
    return {
        ...box,
        x: Math.min(Math.max(box.x, 0), Math.max(0, page.width - box.width)),
        y: Math.min(Math.max(box.y, 0), Math.max(0, page.height - box.height)),
    };
}
/** Whether two boxes describe the same mark, to within `tolerance` points on every edge. */
export function markBoxesMatch(a, b, tolerance = 0.5) {
    return (Math.abs(a.x - b.x) <= tolerance &&
        Math.abs(a.y - b.y) <= tolerance &&
        Math.abs(a.width - b.width) <= tolerance &&
        Math.abs(a.height - b.height) <= tolerance);
}
/** Whether two boxes share any area. Touching edges do not count. */
export function markBoxesOverlap(a, b) {
    return a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height;
}
/**
 * Marks that something in front of them covers, in drawing order (index 0 is at the back).
 *
 * A SEAL is never reported as covered: seals are backgrounds by design — the ACCI seal has
 * a gap printed into it for the date to sit in. What matters is a date or a signature being
 * hidden by something drawn after it.
 */
export function coveredMarks(marks) {
    const found = [];
    marks.forEach((mark, index) => {
        if (!mark.box || mark.slotType === 'SEAL')
            return;
        const cover = marks
            .slice(index + 1)
            .find((front) => front.page === mark.page && front.box && markBoxesOverlap(mark.box, front.box));
        if (cover)
            found.push({ covered: mark, by: cover });
    });
    return found;
}
/**
 * The default drawing position for a new mark of this type — seals at the back, signatures
 * above them, text in front (T-126). Returns the index in a back-to-front list at which to
 * insert it.
 */
export function layerInsertIndex(existing, slotType) {
    const rank = (type) => (type === 'SEAL' ? 0 : type === 'SIGNATURE' ? 1 : 2);
    const mine = rank(slotType);
    // After every mark of the same or a lower rank, so a new seal goes on top of the other
    // seals but still behind any signature or text.
    let index = 0;
    existing.forEach((slot, i) => {
        if (rank(slot.slotType) <= mine)
            index = i + 1;
    });
    return index;
}
//# sourceMappingURL=mark-box.js.map